import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/AuthContext";
import { Link } from "react-router-dom";
import { StatusPill } from "@/components/StatusPill";
import { fmtDateTime, formatMoney } from "@/lib/format";
import EmptyState from "@/components/EmptyState";
import { Music2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ClientPortalPage() {
    const { user } = useAuth();
    // find client by email
    const { data: clients = [] } = useQuery({ queryKey: ["my-client-lookup", user?.email], queryFn: () => apiGet("/clients", { q: user?.email }) });
    const myClient = clients.find((c) => c.email === user?.email);
    const { data: clientData } = useQuery({ enabled: !!myClient?.id, queryKey: ["client", myClient?.id], queryFn: () => apiGet(`/clients/${myClient?.id}`) });

    if (!myClient) {
        return (
            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
                <PageHeader title={`Hi ${user?.name?.split(" ")[0] || ""}`} subtitle="Welcome to your Musook portal." />
                <EmptyState icon={Music2} title="Let’s get you started" message="You don’t have any bookings yet. Chat with our AI agent or use the wizard to book your first session."
                    action={<div className="flex gap-2"><Button asChild><Link to="/chat">Chat to book</Link></Button><Button variant="outline" asChild><Link to="/book">Book with wizard</Link></Button></div>} />
            </div>
        );
    }

    const { bookings = [], projects = [], invoices = [] } = clientData || {};
    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <PageHeader title={`Hi ${user?.name?.split(" ")[0] || ""}`} subtitle="Everything for your projects, sessions and invoices." testId="portal-header" />
            <div className="grid lg:grid-cols-2 gap-6">
                <Card className="p-4 border-border">
                    <h3 className="font-display text-base font-semibold mb-3">Upcoming sessions</h3>
                    <ul className="space-y-2">
                        {bookings.filter((b) => new Date(b.start_utc) >= new Date()).slice(0, 6).map((b) => (
                            <li key={b.id}><Link to={`/bookings/${b.id}`} className="flex items-center justify-between hover:bg-muted/40 -mx-2 px-2 py-1.5 rounded">
                                <div><div className="font-medium">{fmtDateTime(b.start_utc)}</div><div className="text-xs text-muted-foreground">{b.notes?.slice(0, 60) || "Session"}</div></div>
                                <StatusPill status={b.status} />
                            </Link></li>
                        ))}
                        {bookings.filter((b) => new Date(b.start_utc) >= new Date()).length === 0 && <li className="text-sm text-muted-foreground">Nothing coming up.</li>}
                    </ul>
                </Card>
                <Card className="p-4 border-border">
                    <h3 className="font-display text-base font-semibold mb-3">Projects</h3>
                    <ul className="space-y-2">
                        {projects.map((p) => (
                            <li key={p.id}><Link to={`/projects/${p.id}`} className="block hover:bg-muted/40 -mx-2 px-2 py-1.5 rounded">
                                <div className="flex items-center justify-between"><span className="font-medium">{p.name}</span><StatusPill status={p.stage} /></div>
                                <div className="h-1.5 rounded-full bg-muted mt-1"><div className="h-full rounded-full bg-primary" style={{ width: `${p.progress_pct}%` }} /></div>
                            </Link></li>
                        ))}
                        {projects.length === 0 && <li className="text-sm text-muted-foreground">No projects yet.</li>}
                    </ul>
                </Card>
                <Card className="p-4 border-border lg:col-span-2">
                    <h3 className="font-display text-base font-semibold mb-3">Invoices</h3>
                    <ul className="divide-y divide-border">
                        {invoices.length === 0 && <li className="py-2 text-sm text-muted-foreground">No invoices.</li>}
                        {invoices.map((i) => (
                            <li key={i.id}><Link to={`/invoices/${i.id}`} className="flex items-center justify-between py-2 hover:bg-muted/40 px-2 -mx-2 rounded">
                                <div><div className="font-medium">{i.number}</div><div className="text-xs text-muted-foreground">Total {formatMoney(i.total)} · Balance {formatMoney(i.balance)}</div></div>
                                <StatusPill status={i.status} />
                            </Link></li>
                        ))}
                    </ul>
                </Card>
            </div>
        </div>
    );
}
